import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Send, Edit3, Clock, DollarSign, BookOpen, Users, Trophy } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import NotificationService from '../../services/notificationService';
import { ChallengeRequestData } from '../../types/challenge.types';

interface ChallengeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitor: {
    id: string;
    name: string;
    avatar?: string;
    profilePicture?: string;
    rating: number;
    wins: number;
    totalMatches: number;
  };
  mode: 'create' | 'review' | 'edit';
  initialData?: ChallengeRequestData;
  onRequestSent?: (data: ChallengeRequestData) => void;
  onRequestAccepted?: (data: ChallengeRequestData) => void;
  onRequestDeclined?: (reason: string) => void;
  onRequestEdited?: (data: ChallengeRequestData) => void;
}



interface Subject {
  id: string;
  name: string;
  topics: string[];
}

const subjects: Subject[] = [
  {
    id: 'mathematics',
    name: 'Mathematics',
    topics: ['Algebra', 'Geometry', 'Calculus', 'Statistics', 'Trigonometry', 'Arithmetic']
  },
  {
    id: 'science',
    name: 'Science',
    topics: ['Physics', 'Chemistry', 'Biology', 'Earth Science', 'Astronomy']
  },
  {
    id: 'english',
    name: 'English',
    topics: ['Grammar', 'Literature', 'Composition', 'Vocabulary', 'Reading Comprehension']
  },
  {
    id: 'history',
    name: 'History',
    topics: ['World History', 'Ancient Civilizations', 'Modern Era', 'Geography', 'Political Science']
  },
  {
    id: 'general',
    name: 'General Knowledge',
    topics: ['Current Events', 'Pop Culture', 'Technology', 'Sports', 'Arts']
  }
];

const ChallengeRequestModal: React.FC<ChallengeRequestModalProps> = ({
  isOpen,
  onClose,
  competitor,
  mode,
  initialData,
  onRequestSent,
  onRequestAccepted,
  onRequestDeclined,
  onRequestEdited
}) => {
  const { user: authUser, token } = useAuth();
  const [formData, setFormData] = useState<ChallengeRequestData>({
    topic: '',
    subject: '',
    numberOfQuestions: 50,
    stake: 100,
    startTime: '',
    duration: 30,
    mode: 'live',
    message: ''
  });
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [loading, setLoading] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const declineReasons = [
    'High stake amount',
    'Low stake amount',
    'Unexpected time',
    'Topic not interested',
    'Too many questions',
    'Too few questions',
    'Illness',
    'Busy schedule',
    'Other'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.subject) {
        const subject = subjects.find(s => s.id === initialData.subject);
        setSelectedSubject(subject || null);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (formData.subject) {
      const subject = subjects.find(s => s.id === formData.subject);
      setSelectedSubject(subject || null);
    }
  }, [formData.subject]);

  const handleInputChange = (field: keyof ChallengeRequestData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubjectChange = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    setSelectedSubject(subject || null);
    setFormData(prev => ({ ...prev, subject: subjectId, topic: '' }));
  };

  const handleSendRequest = async () => {
    if (!authUser || !token) return;
    
    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request', {
        opponentId: competitor.id,
        area: {
          subject: selectedSubject?.name || formData.subject,
          topic: formData.topic
        },
        mode: formData.mode,
        stake: formData.stake,
        numberOfQuestions: formData.numberOfQuestions,
        startTime: formData.startTime,
        duration: formData.duration,
        message: formData.message
      });

      if (response.data.success) {
        toast.success('Challenge request sent successfully! 🎯');
        onRequestSent?.(formData);
        onClose();
      }
    } catch (error) {
      console.error('Error sending challenge request:', error);
      toast.error('Failed to send challenge request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!authUser || !token) return;
    
    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request/accept', {
        challengeId: initialData?.id || 'temp-id'
      });

      if (response.data.success) {
        toast.success('Challenge accepted! 🎉');
        onRequestAccepted?.(formData);
        onClose();
      }
    } catch (error) {
      console.error('Error accepting challenge request:', error);
      toast.error('Failed to accept challenge request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineRequest = () => {
    setShowDeclineModal(true);
  };

  const handleConfirmDecline = async () => {
    if (!authUser || !token || !declineReason) return;
    
    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request/decline', {
        challengeId: initialData?.id || 'temp-id',
        reason: declineReason
      });

      if (response.data.success) {
        toast.info('Challenge request declined');
        onRequestDeclined?.(declineReason);
        onClose();
      }
    } catch (error) {
      console.error('Error declining challenge request:', error);
      toast.error('Failed to decline challenge request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRequest = () => {
    // Switch to edit mode
    // This will be handled by the parent component
    onRequestEdited?.(formData);
  };

  const handleSendEditedRequest = async () => {
    if (!authUser || !token) return;
    
    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request/edit', {
        challengeId: initialData?.id || 'temp-id',
        opponentId: competitor.id,
        area: {
          subject: selectedSubject?.name || formData.subject,
          topic: formData.topic
        },
        mode: formData.mode,
        stake: formData.stake,
        numberOfQuestions: formData.numberOfQuestions,
        startTime: formData.startTime,
        duration: formData.duration,
        message: formData.message
      });

      if (response.data.success) {
        toast.success('Edited challenge request sent back successfully! 📝');
        onRequestEdited?.(formData);
        onClose();
      }
    } catch (error) {
      console.error('Error editing challenge request:', error);
      toast.error('Failed to send edited challenge request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {mode === 'create' && 'Send Challenge Request'}
            {mode === 'review' && 'Challenge Request Review'}
            {mode === 'edit' && 'Edit Challenge Request'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {mode === 'create' && (
            <>
              <CompetitorInfo>
                <CompetitorAvatar>
                  {(competitor.profilePicture && (competitor.profilePicture.startsWith('http') || competitor.profilePicture.startsWith('data:'))) || 
                   (competitor.avatar && (competitor.avatar.startsWith('http') || competitor.avatar.startsWith('data:'))) ? (
                    <img src={competitor.profilePicture || competitor.avatar} alt={competitor.name} />
                  ) : (
                    <span>{competitor.name.charAt(0)}</span>
                  )}
                </CompetitorAvatar>
                <CompetitorDetails>
                  <CompetitorName>{competitor.name}</CompetitorName>
                  <CompetitorStats>
                    <span>Rating: {competitor.rating}</span>
                    <span>Wins: {competitor.wins}/{competitor.totalMatches}</span>
                  </CompetitorStats>
                </CompetitorDetails>
              </CompetitorInfo>

              <FormSection>
                <FormLabel>Subject</FormLabel>
                <Select
                  value={formData.subject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                >
                  <option value="">Select a subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </Select>
                {formData.subject && (
                  <SelectedValueDisplay>
                    Selected: {subjects.find(s => s.id === formData.subject)?.name}
                  </SelectedValueDisplay>
                )}
              </FormSection>

              {selectedSubject && (
                <FormSection>
                  <FormLabel>Topic</FormLabel>
                  <Select
                    value={formData.topic}
                    onChange={(e) => handleInputChange('topic', e.target.value)}
                  >
                    <option value="">Select a topic</option>
                    {selectedSubject.topics.map(topic => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </Select>
                  {formData.topic && (
                    <SelectedValueDisplay>
                      Selected: {formData.topic}
                    </SelectedValueDisplay>
                  )}
                </FormSection>
              )}

              <FormSection>
                <FormLabel>Number of Questions</FormLabel>
                <Input
                  type="number"
                  min="50"
                  max="100"
                  value={formData.numberOfQuestions}
                  onChange={(e) => handleInputChange('numberOfQuestions', parseInt(e.target.value))}
                />
                <SelectedValueDisplay>
                  Selected: {formData.numberOfQuestions} questions
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Stake Amount (KES)</FormLabel>
                <Input
                  type="number"
                  min="50"
                  max="10000"
                  value={formData.stake}
                  onChange={(e) => handleInputChange('stake', parseInt(e.target.value))}
                />
                <SelectedValueDisplay>
                  Selected: KES {formData.stake}
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Start Time</FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                />
                {formData.startTime && (
                  <SelectedValueDisplay>
                    Selected: {new Date(formData.startTime).toLocaleString()}
                  </SelectedValueDisplay>
                )}
              </FormSection>

              <FormSection>
                <FormLabel>Duration (minutes)</FormLabel>
                <Select
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </Select>
                <SelectedValueDisplay>
                  Selected: {formData.duration} minutes
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Mode</FormLabel>
                <Select
                  value={formData.mode}
                  onChange={(e) => handleInputChange('mode', e.target.value)}
                >
                  <option value="live">Live Competition</option>
                  <option value="timed">Timed Challenge</option>
                </Select>
                <SelectedValueDisplay>
                  Selected: {formData.mode === 'live' ? 'Live Competition' : 'Timed Challenge'}
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Message (Optional)</FormLabel>
                <TextArea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Add a personal message to your challenge request..."
                  rows={3}
                />
              </FormSection>
            </>
          )}

          {mode === 'review' && (
            <>
              <RequestInfo>
                <InfoItem>
                  <BookOpen size={16} />
                  <span><strong>Subject:</strong> {formData.subject}</span>
                </InfoItem>
                <InfoItem>
                  <Trophy size={16} />
                  <span><strong>Topic:</strong> {formData.topic}</span>
                </InfoItem>
                <InfoItem>
                  <Users size={16} />
                  <span><strong>Questions:</strong> {formData.numberOfQuestions}</span>
                </InfoItem>
                <InfoItem>
                  <DollarSign size={16} />
                  <span><strong>Stake:</strong> KES {formData.stake}</span>
                </InfoItem>
                <InfoItem>
                  <Clock size={16} />
                  <span><strong>Start Time:</strong> {new Date(formData.startTime).toLocaleString()}</span>
                </InfoItem>
                <InfoItem>
                  <Clock size={16} />
                  <span><strong>Duration:</strong> {formData.duration} minutes</span>
                </InfoItem>
                {formData.message && (
                  <InfoItem>
                    <span><strong>Message:</strong> {formData.message}</span>
                  </InfoItem>
                )}
              </RequestInfo>

              <ActionButtons>
                <AcceptButton onClick={handleAcceptRequest}>
                  Accept Challenge
                </AcceptButton>
                <EditButton onClick={handleEditRequest}>
                  <Edit3 size={16} />
                  Edit & Send Back
                </EditButton>
                <DeclineButton onClick={handleDeclineRequest}>
                  Decline
                </DeclineButton>
              </ActionButtons>
            </>
          )}

          {mode === 'edit' && (
            <>
              <EditNote>
                You're editing the challenge request. Make your changes and send it back to the challenger.
              </EditNote>
              
              <FormSection>
                <FormLabel>Subject</FormLabel>
                <Select
                  value={formData.subject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                >
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </Select>
                {formData.subject && (
                  <SelectedValueDisplay>
                    Selected: {subjects.find(s => s.id === formData.subject)?.name}
                  </SelectedValueDisplay>
                )}
              </FormSection>

              {selectedSubject && (
                <FormSection>
                  <FormLabel>Topic</FormLabel>
                  <Select
                    value={formData.topic}
                    onChange={(e) => handleInputChange('topic', e.target.value)}
                  >
                    {selectedSubject.topics.map(topic => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </Select>
                  {formData.topic && (
                    <SelectedValueDisplay>
                      Selected: {formData.topic}
                    </SelectedValueDisplay>
                  )}
                </FormSection>
              )}

              <FormSection>
                <FormLabel>Number of Questions</FormLabel>
                <Input
                  type="number"
                  min="50"
                  max="100"
                  value={formData.numberOfQuestions}
                  onChange={(e) => handleInputChange('numberOfQuestions', parseInt(e.target.value))}
                />
                <SelectedValueDisplay>
                  Selected: {formData.numberOfQuestions} questions
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Stake Amount (KES)</FormLabel>
                <Input
                  type="number"
                  min="50"
                  max="10000"
                  value={formData.stake}
                  onChange={(e) => handleInputChange('stake', parseInt(e.target.value))}
                />
                <SelectedValueDisplay>
                  Selected: KES {formData.stake}
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Start Time</FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                />
                {formData.startTime && (
                  <SelectedValueDisplay>
                    Selected: {new Date(formData.startTime).toLocaleString()}
                  </SelectedValueDisplay>
                )}
              </FormSection>

              <FormSection>
                <FormLabel>Duration (minutes)</FormLabel>
                <Select
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </Select>
                <SelectedValueDisplay>
                  Selected: {formData.duration} minutes
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Mode</FormLabel>
                <Select
                  value={formData.mode}
                  onChange={(e) => handleInputChange('mode', e.target.value)}
                >
                  <option value="live">Live Competition</option>
                  <option value="timed">Timed Challenge</option>
                </Select>
                <SelectedValueDisplay>
                  Selected: {formData.mode === 'live' ? 'Live Competition' : 'Timed Challenge'}
                </SelectedValueDisplay>
              </FormSection>

              <FormSection>
                <FormLabel>Message (Optional)</FormLabel>
                <TextArea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Add your message..."
                  rows={3}
                />
              </FormSection>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {mode === 'create' && (
            <SendButton onClick={handleSendRequest} disabled={loading}>
              {loading ? 'Sending...' : (
                <>
                  <Send size={16} />
                  Send Challenge Request
                </>
              )}
            </SendButton>
          )}

          {mode === 'edit' && (
            <SendButton onClick={handleSendEditedRequest} disabled={loading}>
              {loading ? 'Sending...' : (
                <>
                  <Send size={16} />
                  Send Edited Request Back
                </>
              )}
            </SendButton>
          )}

          <CancelButton onClick={onClose}>
            Cancel
          </CancelButton>
        </ModalFooter>
      </ModalContainer>

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <ModalOverlay>
          <ModalContainer style={{ maxWidth: '400px' }}>
            <ModalHeader>
              <ModalTitle>Decline Challenge Request</ModalTitle>
              <CloseButton onClick={() => setShowDeclineModal(false)}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormSection>
                <FormLabel>Reason for declining</FormLabel>
                <Select
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                >
                  <option value="">Select a reason</option>
                  {declineReasons.map(reason => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Select>
              </FormSection>
            </ModalBody>
            <ModalFooter>
              <DeclineButton onClick={handleConfirmDecline} disabled={!declineReason}>
                Confirm Decline
              </DeclineButton>
              <CancelButton onClick={() => setShowDeclineModal(false)}>
                Cancel
              </CancelButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
  
  @media (max-width: 768px) {
    padding: 16px 16px 0 16px;
    padding-bottom: 12px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px 24px;
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
  
  @media (max-width: 768px) {
    padding: 0 16px 16px 16px;
    padding-top: 12px;
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

const CompetitorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`;

const CompetitorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const CompetitorDetails = styled.div`
  flex: 1;
`;

const CompetitorName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #111827;
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CompetitorStats = styled.div`
  display: flex;
  gap: 16px;
  font-size: 0.875rem;
  color: #6b7280;
  
  @media (max-width: 768px) {
    gap: 12px;
    font-size: 0.8rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 6px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 0.8rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 0.8rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 0.8rem;
  }
`;

const RequestInfo = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #374151;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    color: #667eea;
  }
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 10px;
    font-size: 0.875rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.875rem;
  }
`;

const AcceptButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.875rem;
  }
`;

const EditButton = styled.button`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.875rem;
  }
`;

const DeclineButton = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.875rem;
  }
`;

const CancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.875rem;
  }
`;

const EditNote = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
    font-size: 0.8rem;
  }
`;

const SelectedValueDisplay = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  color: #0369a1;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: "✓";
    font-weight: bold;
    color: #059669;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 0.8rem;
    margin-top: 6px;
  }
`;

export default ChallengeRequestModal;
